import type {
  FilterCustomizationDataDto,
  FilterCustomizationDataDtoResponse,
} from '@asset-metrix/portal-gateway-client-js';
import type { QueryClient } from '@tanstack/react-query';

import {
  deleteFilterCustomization,
  filterCustomizationQueryKey,
  mutateFilterCustomization,
  setFilterCustomizationQueryData,
} from '../../api/portalGateway/tableFilterCustomization';

import type { DraftDashboardTile } from './utils/dashboard.types';

type ContentFilterRef = {
  referenceType: 'content';
  reference: string;
};

type ContentFilterUpsert = ContentFilterRef & {
  data: FilterCustomizationDataDto;
};

export type DraftCustomFilterWrites = {
  upserts: ContentFilterUpsert[];
  deletes: ContentFilterRef[];
};

export function planDraftCustomFilterWrites(
  tiles: readonly DraftDashboardTile[]
): DraftCustomFilterWrites {
  const upserts: ContentFilterUpsert[] = [];
  const deletes: ContentFilterRef[] = [];
  for (const tile of tiles) {
    const columnIds = tile.draftCustomFilterColumnIds;
    if (columnIds == null) {
      continue;
    }
    if (columnIds.length === 0) {
      deletes.push({ referenceType: 'content', reference: tile.instanceId });
      continue;
    }
    upserts.push({
      referenceType: 'content',
      reference: tile.instanceId,
      data: {
        columns: columnIds.map((columnId) => ({ columnId })),
      },
    });
  }
  return { upserts, deletes };
}

export function snapshotFilterCustomizationCache(
  queryClient: QueryClient,
  upserts: readonly ContentFilterUpsert[]
): Map<string, FilterCustomizationDataDtoResponse | null | undefined> {
  return new Map(
    upserts.map((item) => [
      item.reference,
      queryClient.getQueryData<FilterCustomizationDataDtoResponse | null>(
        filterCustomizationQueryKey(item.referenceType, item.reference)
      ),
    ])
  );
}

export function writeDraftCustomFilterCache(
  queryClient: QueryClient,
  { upserts, deletes }: DraftCustomFilterWrites
): void {
  for (const item of upserts) {
    setFilterCustomizationQueryData(
      queryClient,
      item.referenceType,
      item.reference,
      item.data
    );
  }
  for (const item of deletes) {
    setFilterCustomizationQueryData(
      queryClient,
      item.referenceType,
      item.reference,
      { columns: [] }
    );
  }
}

export async function rollbackDraftCustomFilterUpserts({
  upserts,
  baselineInstanceIds,
  priors,
}: {
  upserts: readonly ContentFilterUpsert[];
  baselineInstanceIds: ReadonlySet<string>;
  priors: ReadonlyMap<
    string,
    FilterCustomizationDataDtoResponse | null | undefined
  >;
}): Promise<void> {
  await Promise.all(
    upserts.map(async (item) => {
      if (!baselineInstanceIds.has(item.reference)) {
        await deleteFilterCustomization({
          referenceType: item.referenceType,
          reference: item.reference,
        }).catch(() => undefined);
        return;
      }
      const priorColumns = priors.get(item.reference)?.data.columns ?? [];
      if (priorColumns.length === 0) {
        await deleteFilterCustomization({
          referenceType: item.referenceType,
          reference: item.reference,
        }).catch(() => undefined);
        return;
      }
      await mutateFilterCustomization({
        referenceType: item.referenceType,
        reference: item.reference,
        data: { columns: priorColumns },
      }).catch(() => undefined);
    })
  );
}
